import { formatMaterialSummaryAddressForCell } from './format-material-summary-address-for-cell.util';
import { formatMaterialSummaryUomForCell } from './format-material-summary-uom-for-cell.util';

/** Header labels and JSON keys for Material Summary grid output. */
export const MATERIAL_SUMMARY_SHEET_HEADERS: readonly {
    readonly label: string;
    readonly jsonKey: string;
}[] = [
    { label: 'Record type', jsonKey: 'recordType' },
    { label: 'Summary date', jsonKey: 'summaryDate' },
    { label: 'Origin facility code', jsonKey: 'originFacilityCode' },
    { label: 'Origin facility name', jsonKey: 'originFacilityName' },
    { label: 'Origin facility address', jsonKey: 'originFacilityAddress' },
    { label: 'Destination facility code', jsonKey: 'destinationFacilityCode' },
    { label: 'Destination facility name', jsonKey: 'destinationFacilityName' },
    { label: 'Destination facility address', jsonKey: 'destinationFacilityAddress' },
    { label: 'Input material type id', jsonKey: 'inputMaterialTypeId' },
    { label: 'Input material type name', jsonKey: 'inputMaterialTypeName' },
    { label: 'Material type name', jsonKey: 'materialTypeName' },
    { label: 'Report alias', jsonKey: 'reportAlias' },
    { label: 'Quantity', jsonKey: 'quantity' },
    { label: 'UOM', jsonKey: 'uom' },
    { label: 'Processing method name', jsonKey: 'processingMethodName' },
    { label: 'Created on', jsonKey: 'createdOn' },
    { label: 'Updated on', jsonKey: 'updatedOn' },
] as const;

const MATERIAL_SUMMARY_ADDRESS_JSON_KEYS = new Set<string>([
    'originFacilityAddress',
    'destinationFacilityAddress',
]);

/** Coerces API / JSON values to a single Excel cell string. */
function cellString(value: unknown): string {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? String(value) : '';
    }
    if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
    }
    return String(value);
}

function pick(record: Record<string, unknown>, key: string): unknown {
    if (!(key in record)) {
        return undefined;
    }
    return record[key];
}

/**
 * Maps one Get-Material-Summary record object into a row of primitive cell values (column order matches
 * {@link MATERIAL_SUMMARY_SHEET_HEADERS}).
 */
export function materialSummaryRecordToCellValues(record: unknown): string[] {
    if (record === null || typeof record !== 'object') {
        return MATERIAL_SUMMARY_SHEET_HEADERS.map(() => '');
    }
    const r = record as Record<string, unknown>;
    return MATERIAL_SUMMARY_SHEET_HEADERS.map((header) => {
        const raw = pick(r, header.jsonKey);
        if (MATERIAL_SUMMARY_ADDRESS_JSON_KEYS.has(header.jsonKey)) {
            return formatMaterialSummaryAddressForCell(raw);
        }
        if (header.jsonKey === 'uom') {
            return formatMaterialSummaryUomForCell(raw);
        }
        return cellString(raw);
    });
}
